/**
 * SongReviewGradeButtons Component
 *
 * Displays Anki-style grading buttons (0-5) for flashcard review.
 * Buttons are disabled until the flashcard is flipped.
 * Includes keyboard shortcuts hint.
 */

import type { AnkiGrade } from '@/store/slices/songReviewSessionSlice'

interface SongReviewGradeButtonsProps {
  isFlipped: boolean
  onGrade: (grade: AnkiGrade) => void
}

const gradeButtons = [
  { grade: 0, label: 'Forgot', colorClass: 'bg-red-900/20 border-red-500 text-red-500 hover:bg-red-500 hover:text-white' },
  { grade: 1, label: 'Again', colorClass: 'bg-red-700/20 border-red-400 text-red-400 hover:bg-red-400 hover:text-white' },
  { grade: 2, label: 'Hard', colorClass: 'bg-orange-900/20 border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white' },
  { grade: 3, label: 'Good', colorClass: 'bg-yellow-900/20 border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-black' },
  { grade: 4, label: 'Easy', colorClass: 'bg-green-900/20 border-green-500 text-green-500 hover:bg-green-500 hover:text-black' },
  { grade: 5, label: 'Perfect', colorClass: 'bg-blue-900/20 border-blue-500 text-blue-500 hover:bg-blue-500 hover:text-white' },
] as const

export function SongReviewGradeButtons({ isFlipped, onGrade }: SongReviewGradeButtonsProps) {
  return (
    <>
      {/* Grade Buttons */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
        {gradeButtons.map(({ grade, label, colorClass }) => (
          <button
            key={grade}
            onClick={() => onGrade(grade as AnkiGrade)}
            disabled={!isFlipped}
            className={`px-4 py-3 border-2 ${colorClass} disabled:opacity-30 disabled:cursor-not-allowed transition-colors font-mono font-bold`}
          >
            {grade}<br />
            <span className="text-xs">{label}</span>
          </button>
        ))}
      </div>

      {/* Keyboard Shortcuts Hint */}
      <div className="mt-4 terminal-border border-trail-tan p-3 bg-black/50">
        <p className="text-trail-tan font-mono text-xs">
          <strong className="text-sky-blue">[SHORTCUTS]</strong> Click card to flip • Press 0-5 to grade
        </p>
      </div>
    </>
  )
}
